import { location, reactive } from 'cdui-js';
import { postToApp } from './rnBridge';
import type { AgreementSceneDocItem } from '../api/agreement-doc';
import i18n from '../i18n';
import { legalUrlKeepEmbed } from '../pages/legal/legalEmbed';
import { showToast } from '../pages/mobile/components/Toast';
import { showAgreementRichTextSheet } from './show-agreement-rich-text';
import { getCachedAgreementPdf, getCachedAgreementPdfBase64 } from './agreement-pdf-cache';

/** 详情页读取当前协议（避免大段正文塞 query） */
export const agreementDocViewState = reactive({
  doc: null as AgreementSceneDocItem | null,
});

export type AgreementViewKind = 'pdf' | 'html' | 'text' | 'rich_text';

const isInAppWebView = () => !!(window as any).ReactNativeWebView;

const normalizeType = (contentType?: string) => String(contentType || '').toUpperCase().trim();

/** 与旧版 W-8BEN 一致：相对路径拼接图片/文件 CDN 前缀 */
export const resolveAgreementFileUrl = (fileUrl?: string) => {
  const raw = String(fileUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw) || raw.startsWith('blob:') || raw.startsWith('data:')) {
    return raw;
  }
  const base = String(import.meta.env.VITE_IMG_BASE_URL || '');
  if (!base) return raw;
  if (base.endsWith('/') && raw.startsWith('/')) return `${base}${raw.slice(1)}`;
  if (!base.endsWith('/') && !raw.startsWith('/')) return `${base}/${raw}`;
  return `${base}${raw}`;
};

export const looksLikeHtmlBody = (body?: string) => {
  const s = String(body || '').trim();
  if (!s) return false;
  return /^<!DOCTYPE/i.test(s) || /^<[a-z!][\s\S]*>/i.test(s);
};

export { stripAgreementHtmlThemeColors } from './agreement-html-theme';

/** RICH_TEXT：fileUrl 字段直接存 HTML 正文，不是文件地址 */
export const isRichTextContentType = (contentType?: string) => {
  const type = normalizeType(contentType);
  return type === 'RICH_TEXT' || type.includes('RICH');
};

/** 按 contentType + 正文形态判断展示方式 */
export const resolveAgreementViewKind = (
  doc: Pick<AgreementSceneDocItem, 'contentType' | 'fileName' | 'fileUrl'>,
  body?: string,
): AgreementViewKind => {
  const type = normalizeType(doc.contentType);
  if (type === 'PDF' || type.includes('PDF')) return 'pdf';
  // RICH_TEXT：弹层展示 fileUrl 内 HTML
  if (isRichTextContentType(doc.contentType)) return 'rich_text';
  if (type === 'HTML' || type.includes('HTML')) return 'html';
  if (type === 'TEXT' || type.includes('TEXT') || type.includes('PLAIN')) {
    return looksLikeHtmlBody(body) ? 'html' : 'text';
  }

  const name = String(doc.fileName || doc.fileUrl || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'pdf';
  if (name.endsWith('.html') || name.endsWith('.htm')) return 'html';
  if (looksLikeHtmlBody(body)) return 'html';
  return 'text';
};

type OpenPdfPayload = {
  url: string;
  fileName: string;
  docCode?: string;
  base64?: string;
};

const postOpenPdf = (payload: OpenPdfPayload) => {
  const msg = { type: 'WEB_TO_APP_OpenPDF', payload };
  console.log(' postToApp OpenPDF', {
    fileName: payload.fileName,
    docCode: payload.docCode,
    url: payload.url,
    hasBase64: !!payload.base64,
    base64Len: payload.base64?.length ?? 0,
  });
  postToApp(msg);
};

/**
 * 按旧版 W-8BEN 方式浏览 PDF：
 * App 内优先带缓存 base64（秒开）；无缓存 / 转换失败则只传 url（原流程）。
 * 浏览器：有缓存用 blob URL，否则新开页。
 */
export function openAgreementPdf(
  url: string,
  fileName = 'document.pdf',
  docCode?: string,
) {
  const fullUrl = resolveAgreementFileUrl(url);
  if (!fullUrl) {
    showToast(i18n.Common.AgreementFileUrlEmpty);
    return;
  }
  console.log(`协议文件全部地址：fullUrl: ${fullUrl}, isInAppWebView(): ${isInAppWebView()}`);

  void (async () => {
    if (isInAppWebView()) {
      const cached = await getCachedAgreementPdfBase64({ fullUrl, docCode });
      if (cached?.base64) {
        postOpenPdf({
          url: fullUrl,
          fileName: cached.fileName || fileName,
          docCode: cached.docCode || docCode,
          base64: cached.base64,
        });
        return;
      }
      // 原流程：仅 URL
      postOpenPdf({ url: fullUrl, fileName, docCode });
      return;
    }

    // 浏览器：优先用缓存 blob，否则 CDN url（预载可暖 HTTP 缓存）
    const entry = getCachedAgreementPdf({ fullUrl, docCode });
    if (entry?.blob) {
      const objectUrl = URL.createObjectURL(entry.blob);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      // 延迟 revoke，给新窗口一点加载时间
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      return;
    }
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  })();
}

/** 打开协议：PDF 原生/新窗口；RICH_TEXT 弹层；TEXT/HTML 跳转详情页 */
export function openAgreementDoc(doc: AgreementSceneDocItem | null | undefined) {
  console.log('openAgreementDoc', doc);
  if (!doc) {
    showToast(i18n.Common.AgreementUnavailable);
    return;
  }

  const kind = resolveAgreementViewKind(doc, doc.content || doc.fileUrl);
  if (kind === 'pdf') {
    openAgreementPdf(doc.fileUrl || '', doc.fileName || `${doc.docCode}.pdf`, doc.docCode);
    return;
  }

  if (kind === 'rich_text') {
    showAgreementRichTextSheet(doc);
    return;
  }

  // TEXT / HTML：进详情页展示
  agreementDocViewState.doc = doc;
  const q = new URLSearchParams();
  q.set('docCode', doc.docCode);
  if (doc.contentType) q.set('contentType', String(doc.contentType));
  location.routeTo(legalUrlKeepEmbed(`/agreement-doc?${q.toString()}`));
}
